import type { ThemeConfig } from "antd";

// DIT "First in Flight" palette — see ~/.claude/CLAUDE.md
export const ditColors = {
  primary: "#092940",
  primaryHover: "#0c3654",
  secondaryLight: "#EEF7FF",
  secondaryOnDark: "#3892E1",
  secondaryOnWhite: "#1E79C8",
  secondaryDark: "#3B75A9",
  accentLight: "#FFE9EE",
  accent: "#CF1F42",
  accentDark: "#BC2442",
  white: "#FFFFFF",
  // Score-band tokens (paired bg + fg verified for WCAG 2.1 AA contrast).
  scoreRedBg: "#FFE9EE",
  scoreRedFg: "#A11A37",
  scoreYellowBg: "#FFF4D1",
  scoreYellowFg: "#5C4200",
  scoreGreenBg: "#E6F4EA",
  scoreGreenFg: "#1F6B3A",
} as const;

export const ditTheme: ThemeConfig = {
  token: {
    colorPrimary: ditColors.primary,
    colorLink: ditColors.secondaryOnWhite,
    colorLinkHover: ditColors.primary,
    colorError: ditColors.accent,
    colorInfo: ditColors.secondaryOnWhite,
    colorBgLayout: "#f6f7f9",
    colorBorder: "rgba(9, 41, 64, 0.08)",
    colorBorderSecondary: "rgba(9, 41, 64, 0.06)",
    fontFamily:
      '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    borderRadius: 8,
  },
  components: {
    Layout: {
      // OAIP-79: TopNav now renders its own surface (apex-topnav) rather than
      // relying on Layout.Header chrome. Keeping a transparent default here
      // so any future Layout.Header usage doesn't pick up a stale dark navy.
      headerBg: "transparent",
      headerColor: ditColors.primary,
      headerHeight: 60,
      siderBg: ditColors.primary,
      bodyBg: "#f6f7f9",
    },
    Menu: {
      darkItemBg: ditColors.primary,
      darkItemSelectedBg: ditColors.primaryHover,
    },
    Button: {
      colorPrimary: ditColors.primary,
      colorPrimaryHover: ditColors.secondaryDark,
      colorPrimaryActive: ditColors.primaryHover,
      defaultBorderColor: "rgba(9, 41, 64, 0.16)",
    },
    Card: {
      // Old defaults: 24px padding + 1px solid #f0f0f0 border + box-shadow.
      // OAIP-79 surfaces are denser; full overrides live in surfaces.css.
      paddingLG: 20,
      borderRadiusLG: 8,
    },
    Tag: {
      defaultBg: "rgba(9, 41, 64, 0.06)",
      defaultColor: ditColors.primary,
    },
    Table: {
      headerBg: "transparent",
      headerColor: "#56657a",
      headerSplitColor: "transparent",
      borderColor: "rgba(9, 41, 64, 0.06)",
      rowHoverBg: "#fbfbfd",
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Statistic: {
      titleFontSize: 11,
      contentFontSize: 28,
    },
    Select: {
      // Antd's default derives optionSelectedBg as a muted grey (#787e80) from
      // our dark navy primary, which lands ~4.0:1 against near-black text and
      // fails WCAG AA. Pin to the DIT-approved Sec. Light + Primary pairing.
      optionSelectedBg: ditColors.secondaryLight,
      optionSelectedColor: ditColors.primary,
      optionActiveBg: "rgba(9, 41, 64, 0.08)",
    },
    Input: {
      activeBorderColor: ditColors.secondaryOnWhite,
      hoverBorderColor: "rgba(9, 41, 64, 0.32)",
    },
  },
};
